import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Stepper, Step, StepLabel, StepContent, Box, Grid, Button, Paper, Typography, Alert, AlertTitle } from '@mui/material';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { SendingOrderEdit, SendingOrderEditSchema } from 'src/modules/sending/models/sendingOrderEdit';
import { SendingOrder } from 'src/modules/sending/models/sendingOrder';
import { updateSendingOrder, getSendingOrderById } from 'src/shared/services/sendingService';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import SendingTypeForm from 'src/modules/sending/components/sendingTypeForm';
import SendingOrderCustomerForm from 'src/modules/sending/components/sendingOrderCustomerForm';
import SendingAttachmentForm from 'src/modules/sending/components/sendingAttachmentForm';
import { SendingOrderFormSummary } from 'src/modules/sending/components/sendingOrderFormSummary';
import ConsoleHelper from 'src/shared/functions/consoleHelper';

function getSteps() {
  return ['Typ av utskick', 'Beställare', 'Create an ad'];
}

// Not used
const SendingOrderEditPage = () => {
  const formMethods = useForm<SendingOrderEdit>({
    resolver: yupResolver(SendingOrderEditSchema),
    mode: 'all',
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
  } = formMethods;

  const [sendingOrder, setSendingOrder] = useState<SendingOrder>();
  const { id } = useParams();

  useEffect(() => {
    const fetchData = async () => {
      const result = await getSendingOrderById(id);
      ConsoleHelper.log('fetch', result.data);
      setSendingOrder(result.data);
    };
    if (id) {
      fetchData();
    }
  }, [id]);

  const [activeStep, setActiveStep] = React.useState(0);
  const steps = getSteps();

  const handleNext = async () => {
    await trigger();
    if (Object.keys(errors).length === 0) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = async () => {
    await trigger();
    if (Object.keys(errors).length === 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  // const handleReset = () => {
  //   setActiveStep(0);
  // };

  useEffect(() => {
    if (sendingOrder) {
      reset(sendingOrder);
    }
  }, [reset, sendingOrder]);

  const updateFormData = (data) => {
    ConsoleHelper.log('updateFormData', data);
    const fetchData = async () => {
      await updateSendingOrder({ ...data, updated: new Date(), updatedByUser: 1 } as SendingOrder);
      toast.success('Ordern uppdaterades!', {
        position: 'bottom-left',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      handleNext();
    };
    fetchData();
  };

  const onSubmit = (data) => {
    trigger();
    updateFormData(data);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GridWrapper>
          <Grid item xs={12} md={4} style={{ display: 'flex' }}>
            <MainContentHeader title='Granska postutskick' />
          </Grid>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
                xs: {
                  p: 1,
                },
              }}
            >
              <Grid container>
                <Grid
                  xs={12}
                  xl={8}
                  sx={{
                    order: 2,
                    sm: {
                      order: 1,
                    },
                  }}
                  item
                >
                  <Stepper activeStep={activeStep} orientation='vertical' sx={{ p: 1 }}>
                    <Step key={1}>
                      <StepLabel>
                        <Typography variant='h6'>Typ av utskick</Typography>
                      </StepLabel>
                      <StepContent>
                        <Box
                          sx={{
                            m: 2,
                            xs: {
                              m: 0,
                            },
                          }}
                        >
                          <GridWrapper gutterBottom>
                            <SendingTypeForm />
                            <Grid item xs={12}>
                              <Button
                                variant='contained'
                                color='primary'
                                onClick={handleNext}
                                sx={{
                                  mt: 1,
                                  mr: 1,
                                }}
                              >
                                Nästa
                              </Button>
                            </Grid>
                          </GridWrapper>
                        </Box>
                      </StepContent>
                    </Step>
                    <Step key={2}>
                      <StepLabel>
                        <Typography variant='h6'>Bilagor</Typography>
                      </StepLabel>
                      <StepContent>
                        <Box
                          sx={{
                            m: 2,
                            xs: {
                              m: 0,
                            },
                          }}
                        >
                          <GridWrapper gutterBottom>
                            <SendingAttachmentForm />
                            <Grid item xs={12}>
                              <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                                Tillbaka
                              </Button>
                              <Button variant='contained' color='primary' onClick={handleNext} sx={{ mt: 1, mr: 1 }}>
                                {activeStep === steps.length - 1 ? 'Skicka beställning' : 'Nästa'}
                              </Button>
                            </Grid>
                          </GridWrapper>
                        </Box>
                      </StepContent>
                    </Step>
                    <Step key={3}>
                      <StepLabel>
                        <Typography variant='h6'>Beställare</Typography>
                      </StepLabel>
                      <StepContent>
                        <Box
                          sx={{
                            m: 2,
                            xs: {
                              m: 0,
                            },
                          }}
                        >
                          <GridWrapper gutterBottom>
                            <SendingOrderCustomerForm />
                            <Grid item xs={12}>
                              <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
                                Tillbaka
                              </Button>
                              {activeStep === steps.length - 1 ? (
                                <Button variant='contained' color='primary' type='submit' sx={{ mt: 1, mr: 1 }}>
                                  Uppdatera beställning
                                </Button>
                              ) : (
                                <Button variant='contained' color='primary' onClick={handleNext} sx={{ mt: 1, mr: 1 }}>
                                  {activeStep === steps.length - 1 ? 'Skicka beställning' : 'Nästa'}
                                </Button>
                              )}
                            </Grid>
                          </GridWrapper>
                        </Box>
                      </StepContent>
                    </Step>
                  </Stepper>
                  {activeStep === steps.length && (
                    <Grid xs={12} item>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                        }}
                      >
                        <Alert severity='success'>
                          {id && <AlertTitle>Beställningen uppdaterades!</AlertTitle>}
                          {!id && <AlertTitle>Tack för din beställning!</AlertTitle>}
                        </Alert>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  xs={12}
                  xl={4}
                  sx={{
                    order: 1,
                    sm: {
                      order: 2,
                    },
                  }}
                  item
                >
                  <GridWrapper>
                    <Grid xs={12} md={6} xl={12} item>
                      <FormErrorSummary translationNamespace='xx' errors={errors} />
                    </Grid>
                    <Grid xs={12} md={6} xl={12} item>
                      <SendingOrderFormSummary
                        formSummaryList={[
                          {
                            label: 'Typ av utskick',
                            fieldName: 'product_id',
                          },
                          {
                            label: 'Utskicksdatum',
                            fieldName: 'date_id',
                          },
                          {
                            label: 'Beställare, ditt namn',
                            fieldName: 'createdByName',
                          },
                          {
                            label: 'Beställare, din e-post',
                            fieldName: 'createdByEmail',
                          },
                        ]}
                      />
                    </Grid>
                  </GridWrapper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </GridWrapper>
      </form>
    </FormProvider>
  );
};

export default SendingOrderEditPage;
